import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Ban, Check, Loader2, Pause, Play, RotateCcw, UploadCloud, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useProjects } from "@/hooks/use-projects";
import { useQueryClient } from "@tanstack/react-query";
import {
  abortAllInFlightOnUnload,
  ACCEPTED_MIME_TYPES,
  discardResumableUpload,
  fingerprintFile,
  listResumeStates,
  type ResumeState,
  uploadFile,
} from "@/lib/upload";
import { formatBytes } from "@/lib/utils";

type Props = {
  /** "/app/projects" or "/admin/projects" — where "View project" links after a successful upload. */
  projectsBasePath: string;
};

type QueueStatus =
  | "queued"
  | "uploading"
  | "paused"
  | "done"
  | "error"
  | "cancelled"
  | "interrupted"; // reconstructed from localStorage after a page reload

type QueueItem = {
  id: string;
  /** Absent for rows reconstructed after a reload — the browser drops the file
   *  bytes, so Resume/Retry must re-open the file picker to get them back. */
  file?: File;
  fileName: string;
  fileSize: number;
  /** `name|size|lastModified` — used to match a re-picked file to this row. */
  fingerprint?: string;
  status: QueueStatus;
  progress: number; // 0..1
  error?: string;
  /** Present for multipart uploads once /initiate returns. */
  sessionId?: string;
  /** Smoothed upload speed in bytes/sec, shown while uploading. */
  speedBps?: number;
  /** Present only for reconstructed (interrupted) rows. Carries the stored
   *  projectId/folderId/parts so Resume/Discard can act without the File. */
  resumeState?: ResumeState;
};

function nextId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Fraction already uploaded, derived from completed parts in a resume state. */
function resumeFraction(state: ResumeState): number {
  const done = state.parts.reduce((acc, p) => {
    const start = (p.partNumber - 1) * state.partSize;
    const end = Math.min(start + state.partSize, state.fileSize);
    return acc + (end - start);
  }, 0);
  return state.fileSize > 0 ? done / state.fileSize : 0;
}

/**
 * Format bytes/sec for the UI. Uses MB/s above 1 MB/s, otherwise KB/s. Empty
 * string for garbage inputs so callers can concat without a fallback.
 */
function formatSpeed(bytesPerSec: number): string {
  if (!Number.isFinite(bytesPerSec) || bytesPerSec <= 0) return "";
  const mbps = bytesPerSec / (1024 * 1024);
  if (mbps >= 1) return `${mbps.toFixed(1)} MB/s`;
  const kbps = bytesPerSec / 1024;
  return `${Math.max(1, Math.round(kbps))} KB/s`;
}

/** Shared class for the small round icon buttons on each row. */
function iconBtnClass(disabled: boolean, tone: "default" | "danger" = "default"): string {
  if (disabled) {
    return "grid h-7 w-7 shrink-0 place-items-center rounded-md text-muted-foreground/25 cursor-not-allowed";
  }
  if (tone === "danger") {
    return "grid h-7 w-7 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive";
  }
  return "grid h-7 w-7 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground";
}

export function UploadPage({ projectsBasePath }: Props) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: projects, isLoading: projectsLoading } = useProjects();

  const [projectId, setProjectId] = useState(params.get("projectId") ?? "");
  const folderId = params.get("folderId") || null;
  const isFolderMode = params.get("mode") === "folder";
  // Version upload: `versionOf` is the parent (stack) video id. When set, every
  // uploaded file is stacked as a new version of that asset instead of a new
  // root. Project is fixed (came from the asset), so we hide the picker.
  const versionOf = params.get("versionOf") || undefined;
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const resumeInputRef = useRef<HTMLInputElement>(null);

  // Per-item AbortController for pause/cancel. Kept in a ref so aborting
  // doesn't force a re-render; queue-item ID is the key.
  const abortersRef = useRef<Map<string, AbortController>>(new Map());
  // Why a given abort fired: "pause" → row becomes Paused, "cancel" → Cancelled.
  // Read in the upload promise's .catch to pick the resulting status.
  const intentRef = useRef<Map<string, "pause" | "cancel">>(new Map());
  // Which row + action is waiting on the file picker (post-reload resume/retry).
  const pendingResumeRef = useRef<{ itemId: string; mode: "resume" | "retry" } | null>(null);

  // Rolling window of (timestamp, uploadedBytes) samples per queue item,
  // used to compute a smoothed upload speed.
  const speedSamplesRef = useRef<Map<string, { t: number; bytes: number }[]>>(new Map());

  function computeSpeed(itemId: string, uploadedBytes: number): number {
    const now = performance.now();
    const samples = speedSamplesRef.current.get(itemId) ?? [];
    samples.push({ t: now, bytes: uploadedBytes });
    const cutoff = now - 3000;
    while (samples.length > 1 && samples[0].t < cutoff) samples.shift();
    speedSamplesRef.current.set(itemId, samples);
    if (samples.length < 2) return 0;
    const first = samples[0];
    const last = samples[samples.length - 1];
    const dt = (last.t - first.t) / 1000;
    const db = last.bytes - first.bytes;
    return dt > 0 && db > 0 ? db / dt : 0;
  }

  function clearSpeedSamples(itemId: string) {
    speedSamplesRef.current.delete(itemId);
  }

  function updateItem(id: string, patch: Partial<QueueItem>) {
    setQueue((cur) => cur.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  }

  function removeItem(id: string) {
    setQueue((cur) => cur.filter((item) => item.id !== id));
    abortersRef.current.delete(id);
    intentRef.current.delete(id);
    clearSpeedSamples(id);
  }

  // Hydrate interrupted uploads (from a previous session) as rows, so they get
  // the same Resume / Retry / Discard controls as everything else. Filters out
  // sessions past the server TTL (listResumeStates does that purge).
  useEffect(() => {
    const states = listResumeStates();
    if (states.length === 0) return;
    setQueue((cur) => {
      const seen = new Set(cur.map((i) => i.fingerprint));
      const rows: QueueItem[] = states
        .map((s) => ({ s, fp: `${s.fileName}|${s.fileSize}|${s.lastModified}` }))
        .filter(({ fp }) => !seen.has(fp))
        .map(({ s, fp }) => ({
          id: nextId(),
          fileName: s.fileName,
          fileSize: s.fileSize,
          fingerprint: fp,
          status: "interrupted" as QueueStatus,
          progress: resumeFraction(s),
          sessionId: s.sessionId,
          resumeState: s,
        }));
      return [...rows, ...cur];
    });
  }, []);

  // beforeunload — best-effort abort of every in-flight upload as the tab
  // closes. Does NOT abort the server session (see upload.ts) so resume works.
  useEffect(() => {
    const handler = () => {
      abortAllInFlightOnUnload();
    };
    window.addEventListener("beforeunload", handler);
    window.addEventListener("pagehide", handler);
    return () => {
      window.removeEventListener("beforeunload", handler);
      window.removeEventListener("pagehide", handler);
    };
  }, []);

  // Auto-navigate on success: once every queued item has uploaded (and none
  // failed / are still going), leave the upload page and land the user on the
  // project — or the folder, if this was a folder upload. Guarded so it fires
  // once. A single failure keeps the user here to retry.
  const navigatedRef = useRef(false);
  useEffect(() => {
    if (navigatedRef.current || queue.length === 0 || !projectId) return;
    if (!queue.every((i) => i.status === "done")) return;
    navigatedRef.current = true;
    const dest = folderId
      ? `${projectsBasePath}/${projectId}/folders/${folderId}`
      : `${projectsBasePath}/${projectId}`;
    const t = window.setTimeout(() => navigate(dest), 700);
    return () => window.clearTimeout(t);
  }, [queue, projectId, folderId, projectsBasePath, navigate]);

  const startUpload = useCallback(
    (item: QueueItem, targetProjectId: string, targetFolderId: string | null = folderId) => {
      if (!item.file) return;
      const file = item.file;
      updateItem(item.id, { status: "uploading", error: undefined });
      // Fresh AbortController per attempt.
      const controller = new AbortController();
      abortersRef.current.set(item.id, controller);
      intentRef.current.delete(item.id);

      uploadFile(
        file,
        { projectId: targetProjectId, folderId: targetFolderId, parentVideoId: versionOf },
        (fraction) => {
          const uploadedBytes = fraction * item.fileSize;
          const speedBps = computeSpeed(item.id, uploadedBytes);
          updateItem(item.id, { progress: fraction, speedBps });
        },
        {
          signal: controller.signal,
          onSessionCreated: (sessionId) => {
            updateItem(item.id, { sessionId });
          },
        }
      )
        .then(() => {
          abortersRef.current.delete(item.id);
          clearSpeedSamples(item.id);
          updateItem(item.id, {
            status: "done",
            progress: 1,
            speedBps: undefined,
            resumeState: undefined,
          });
          qc.invalidateQueries({ queryKey: ["projects", targetProjectId, "videos"] });
          if (targetFolderId) {
            qc.invalidateQueries({ queryKey: ["folders", targetFolderId, "contents"] });
          }
          if (versionOf) {
            // Refresh the stack's version list so the new version shows up.
            qc.invalidateQueries({ queryKey: ["videos", versionOf, "versions"] });
          }
        })
        .catch((err: Error) => {
          abortersRef.current.delete(item.id);
          clearSpeedSamples(item.id);
          if (err.name === "AbortError") {
            // Distinguish a Pause from a Cancel by the intent recorded when the
            // abort was triggered. Default to cancelled if we somehow missed it.
            const intent = intentRef.current.get(item.id);
            intentRef.current.delete(item.id);
            updateItem(item.id, {
              status: intent === "pause" ? "paused" : "cancelled",
              error: undefined,
              speedBps: undefined,
            });
          } else {
            updateItem(item.id, { status: "error", error: err.message, speedBps: undefined });
          }
        });
    },
    [qc, folderId, versionOf]
  );

  // ── Row actions ────────────────────────────────────────────────────────

  function pauseUpload(item: QueueItem) {
    intentRef.current.set(item.id, "pause");
    abortersRef.current.get(item.id)?.abort();
  }

  /** Soft cancel — stops the transfer but keeps the session so the row can still
   *  Resume. The R2 session is only released via Discard (×). */
  function cancelUpload(item: QueueItem) {
    intentRef.current.set(item.id, "cancel");
    abortersRef.current.get(item.id)?.abort();
  }

  function resumeUpload(item: QueueItem) {
    const targetProjectId = item.resumeState?.projectId ?? projectId;
    if (!targetProjectId) return;
    const targetFolderId = item.resumeState?.folderId ?? folderId;
    if (item.file) {
      startUpload(item, targetProjectId, targetFolderId);
    } else {
      // Post-reload: bytes are gone, ask the user to re-pick the same file.
      pendingResumeRef.current = { itemId: item.id, mode: "resume" };
      resumeInputRef.current?.click();
    }
  }

  async function retryUpload(item: QueueItem) {
    const targetProjectId = item.resumeState?.projectId ?? projectId;
    if (!targetProjectId) return;
    const targetFolderId = item.resumeState?.folderId ?? folderId;
    // Wipe any existing session/parts so the upload starts from 0%.
    const state = resumeStateFor(item);
    if (state) await discardResumableUpload(state);
    updateItem(item.id, { progress: 0, sessionId: undefined, resumeState: undefined });
    if (item.file) {
      startUpload({ ...item, sessionId: undefined, resumeState: undefined }, targetProjectId, targetFolderId);
    } else {
      pendingResumeRef.current = { itemId: item.id, mode: "retry" };
      resumeInputRef.current?.click();
    }
  }

  async function discardItem(item: QueueItem) {
    const state = resumeStateFor(item);
    if (state) await discardResumableUpload(state);
    removeItem(item.id);
  }

  /** Build a ResumeState good enough for discard/abort from whatever we hold. */
  function resumeStateFor(item: QueueItem): ResumeState | null {
    if (item.resumeState) return item.resumeState;
    if (item.sessionId && item.file) {
      return {
        fileName: item.file.name,
        fileSize: item.file.size,
        lastModified: item.file.lastModified,
        sessionId: item.sessionId,
      } as ResumeState;
    }
    return null;
  }

  async function handleResumePick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    const pending = pendingResumeRef.current;
    pendingResumeRef.current = null;
    if (!file || !pending) return;
    const item = queue.find((i) => i.id === pending.itemId);
    if (!item) return;

    if (item.fingerprint && fingerprintFile(file) !== item.fingerprint) {
      updateItem(item.id, {
        error: "That's a different file — choose the exact same file to resume.",
      });
      return;
    }

    const targetProjectId = item.resumeState?.projectId ?? projectId;
    if (!targetProjectId) return;
    const targetFolderId = item.resumeState?.folderId ?? folderId;

    if (pending.mode === "retry") {
      const state = resumeStateFor(item);
      if (state) await discardResumableUpload(state);
      updateItem(item.id, { progress: 0, sessionId: undefined, resumeState: undefined });
    }
    const merged: QueueItem = {
      ...item,
      file,
      error: undefined,
      ...(pending.mode === "retry" ? { sessionId: undefined, resumeState: undefined } : {}),
    };
    setQueue((cur) => cur.map((i) => (i.id === item.id ? merged : i)));
    startUpload(merged, targetProjectId, targetFolderId);
  }

  // ── File intake ──────────────────────────────────────────────────────

  function addFiles(files: FileList | File[]) {
    const items: QueueItem[] = Array.from(files).map((file) => ({
      id: nextId(),
      file,
      fileName: file.name,
      fileSize: file.size,
      fingerprint: fingerprintFile(file),
      status: "queued" as QueueStatus,
      progress: 0,
    }));
    setQueue((cur) => [...cur, ...items]);
    if (projectId) {
      items.forEach((item) => startUpload(item, projectId));
    }
  }

  function handleFileInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files?.length) addFiles(e.target.files);
    e.target.value = "";
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  }

  const queuedWaitingOnProject = queue.filter((i) => i.status === "queued");
  const doneCount = queue.filter((i) => i.status === "done").length;

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 md:px-8 md:py-10">
      <h1 className="font-display text-3xl font-bold tracking-tight">
        {versionOf ? "Upload new version" : isFolderMode ? "Upload folder" : "Upload"}
      </h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Supported: MP4 video, MP3/WAV/M4A audio, JPEG/PNG/WEBP image.
      </p>

      {versionOf ? (
        <div className="mt-6 rounded-md border border-border bg-secondary/40 px-4 py-3 text-sm text-muted-foreground">
          The file you add here becomes the newest version of this asset. It stacks
          onto the existing one — the previous versions stay accessible from the
          version switcher.
        </div>
      ) : (
        <div className="mt-6 flex flex-col gap-1.5">
          <label htmlFor="upload-project-select" className="text-sm font-medium text-foreground">
            Project
          </label>
          <select
            id="upload-project-select"
            value={projectId}
            disabled={projectsLoading}
            onChange={(e) => {
              const next = e.target.value;
              setProjectId(next);
              if (next) queuedWaitingOnProject.forEach((item) => startUpload(item, next));
            }}
            className="h-10 rounded-md border border-input bg-transparent px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="" disabled>
              {projectsLoading ? "Loading projects…" : "Choose a project"}
            </option>
            {projects?.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          {!projectId && queue.length === 0 && (
            <p className="text-xs text-muted-foreground">
              Pick a project first, or add files below — they'll wait until you do.
            </p>
          )}
        </div>
      )}

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`mt-6 flex cursor-pointer flex-col items-center gap-3 rounded-xl border-2 border-dashed py-16 text-center transition-colors ${
          dragActive ? "border-brand bg-brand-soft/40" : "border-border hover:bg-secondary/30"
        }`}
      >
        <div className="grid h-12 w-12 place-items-center rounded-full bg-secondary text-muted-foreground">
          <UploadCloud className="h-6 w-6" strokeWidth={1.5} />
        </div>
        <p className="text-sm text-muted-foreground">Drag files here, or click to browse.</p>
        <Button type="button" size="sm">
          Choose files
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          multiple={!versionOf}
          accept={ACCEPTED_MIME_TYPES.join(",")}
          className="hidden"
          onChange={handleFileInputChange}
          // @ts-ignore — non-standard attribute, supported by Chrome/Firefox/Safari.
          webkitdirectory={isFolderMode && !versionOf ? "" : undefined}
        />
      </div>

      {/* Hidden picker used to re-attach the file for a post-reload resume/retry. */}
      <input
        ref={resumeInputRef}
        type="file"
        accept={ACCEPTED_MIME_TYPES.join(",")}
        className="hidden"
        onChange={handleResumePick}
      />

      {queue.length > 0 && (
        <div className="mt-6 flex flex-col gap-2">
          {queue.map((item) => {
            const isUploading = item.status === "uploading";
            const isPaused = item.status === "paused";
            const isErrored =
              item.status === "error" ||
              item.status === "cancelled" ||
              item.status === "interrupted";
            const isDone = item.status === "done";
            const canPause = isUploading;
            const canResume = isPaused || isErrored;
            const canRetry = isErrored;
            const needsSameFileNote =
              (isPaused || isErrored) && !item.file && item.status !== "error";

            return (
              <div
                key={item.id}
                className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2.5"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-foreground">{item.fileName}</div>
                  <div className="text-xs text-muted-foreground">
                    {formatBytes(item.fileSize)}
                    {isUploading && (
                      <>
                        {" · "}
                        <span className="tabular-nums">{Math.round(item.progress * 100)}%</span>
                        {item.speedBps && item.speedBps > 0 ? (
                          <>
                            {" · "}
                            <span className="tabular-nums">{formatSpeed(item.speedBps)}</span>
                          </>
                        ) : null}
                      </>
                    )}
                    {isPaused ? ` · paused at ${Math.round(item.progress * 100)}%` : null}
                    {item.status === "interrupted"
                      ? ` · interrupted at ${Math.round(item.progress * 100)}%`
                      : null}
                    {item.status === "cancelled" ? " · cancelled" : null}
                    {item.status === "error" && item.error ? ` · ${item.error}` : null}
                    {item.status === "queued" ? " · waiting for a project" : null}
                  </div>
                  {(isUploading || isPaused || item.status === "interrupted") && (
                    <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-secondary">
                      <div
                        className={`h-full rounded-full transition-all ${
                          isPaused || item.status === "interrupted" ? "bg-muted-foreground/50" : "bg-brand"
                        }`}
                        style={{ width: `${Math.round(item.progress * 100)}%` }}
                      />
                    </div>
                  )}
                  {needsSameFileNote && (
                    <div className="mt-1 text-[11px] text-muted-foreground">
                      Choose the same file again to resume the upload.
                    </div>
                  )}
                  {item.error && item.status !== "error" ? (
                    <div className="mt-1 text-[11px] text-destructive">{item.error}</div>
                  ) : null}
                </div>

                {isUploading && (
                  <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
                )}
                {isDone && <Check className="h-4 w-4 shrink-0 text-emerald-500" />}

                {/* Pause / Resume / Retry — always shown (except when done), greyed
                    when not applicable so the control set stays stable. */}
                {!isDone && (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      aria-label="Pause upload"
                      title="Pause"
                      disabled={!canPause}
                      onClick={() => pauseUpload(item)}
                      className={iconBtnClass(!canPause)}
                    >
                      <Pause className="h-3.5 w-3.5" strokeWidth={1.75} />
                    </button>
                    <button
                      type="button"
                      aria-label="Resume upload"
                      title="Resume"
                      disabled={!canResume}
                      onClick={() => resumeUpload(item)}
                      className={iconBtnClass(!canResume)}
                    >
                      <Play className="h-3.5 w-3.5" strokeWidth={1.75} />
                    </button>
                    <button
                      type="button"
                      aria-label="Retry from start"
                      title="Retry from start"
                      disabled={!canRetry}
                      onClick={() => retryUpload(item)}
                      className={iconBtnClass(!canRetry)}
                    >
                      <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.75} />
                    </button>
                  </div>
                )}

                {/* Trailing action: Cancel while uploading, otherwise Discard/Remove. */}
                {isUploading ? (
                  <button
                    type="button"
                    aria-label="Cancel upload"
                    title="Cancel"
                    onClick={() => cancelUpload(item)}
                    className={iconBtnClass(false, "danger")}
                  >
                    <Ban className="h-3.5 w-3.5" strokeWidth={1.75} />
                  </button>
                ) : (
                  <button
                    type="button"
                    aria-label="Discard"
                    title="Discard"
                    onClick={() => discardItem(item)}
                    className={iconBtnClass(false, "danger")}
                  >
                    <X className="h-3.5 w-3.5" strokeWidth={1.75} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {doneCount > 0 && doneCount === queue.length && (
        <div className="mt-6 flex items-center gap-2 rounded-lg border border-border bg-secondary/40 px-4 py-3 text-sm text-foreground">
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
          {doneCount} {doneCount === 1 ? "file" : "files"} uploaded — opening{" "}
          {folderId ? "folder" : "project"}…
        </div>
      )}
    </div>
  );
}
